import {isSearchPage, useProductListContext} from "../productListProvider/ProductListProvider";
import SearchHeadline from "./SearchHeadline";
import ProductSortInput from "./ProductSortInput";
import SelectedFilterOverview from "./SelectedFilterOverview";
import ListItems from "../productList/ListItems";
import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {ProductListFilterDesktopType, RefType} from "./ProductListFilter";
import window from "../../lib/WindowExtended";
import {filterItemArray} from "../productListProvider/helpers/filterItemArray";

const ProductListFilterDesktop: React.ForwardRefRenderFunction<RefType, ProductListFilterDesktopType> = (
  {filterNumbers, toggleFilter, productListStartPosition},
  ref,
) => {
  const {state, dispatch} = useProductListContext();
  const {t} = useTranslation();
  const [openFilter, setOpenFilter] = useState(true);
  const filterHeadRef = useRef<HTMLDivElement>(null);
  const productListRef = useRef<HTMLDivElement>(null);
  const filterOverviewRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    filterHeadRef,
    productListRef,
  }));

  useEffect(() => {
    setOpenFilter(state.showFilters);
  }, [state.showFilters]);

  useEffect(() => {
    if (!state.loading && openFilter) {
      dispatch({type: "SHOW_FILTERS", payload: true});
    }
  }, [state.loading]);

  // Just some dummies, to keep the type safety, see
  // `ProductListFilterMobile.tsx` and `filterItemArray.tsx`
  const onValueSelect = () => undefined;
  const onRangeSelect = () => undefined;

  // pre-call of the filterItemsArray() function, just to get the length of the result
  const [filterItems, booleanFilterItems] = useMemo(
    () => filterItemArray({state, onValueSelect, onRangeSelect}),
    [state.filters, state.items],
  );
  const filterLength = filterItems.length + booleanFilterItems.length;
  const matchingItems = filterLength > 0 || state.loading;

  const toggleFilterView = () => {
    setOpenFilter(!state.showFilters);
    // Scroll back to actual product list position
    if (window.scrollY > productListStartPosition + 450) {
      window.scrollBy(
        0,
        openFilter
          ? -Math.ceil((window.scrollY - productListStartPosition) / 4)
          : Math.ceil((window.scrollY - productListStartPosition) / 3),
      );
    }
    toggleFilter();
  };

  // reset-section placeholder (necessary so that the product list always starts at the correct position)
  const FilterOverviewPlaceholder: React.FC = () => {
    const [productListPosition, setProductListPosition] = useState(filterOverviewRef.current?.clientHeight || 0);

    useEffect(() => {
      if (filterOverviewRef.current) {
        setProductListPosition(filterOverviewRef.current.clientHeight);
      }
    }, [filterNumbers, filterOverviewRef.current?.clientHeight]);

    return (
      <div className="filter-remove__placeholder" style={{height: `${productListPosition}px`}}>
        {/* empty placeholder */}
      </div>
    );
  };

  return (
    <>
      {isSearchPage && <SearchHeadline />}
      <div className={openFilter ? "column-m-3" : "column-8 column-l-12 filter-list--sticky"}>
        <div className={`filter-list ${openFilter ? "filter-list--sticky" : ""}`}>
          <div ref={filterHeadRef} className="filter-list__head">
            <div className="filter-list__sort-section">
              {matchingItems && (
                <div className="filter-elements__toggle-btn" onClick={toggleFilterView}>
                  <i className="icon icon-filter icon-size--22 mr-3" />
                  <span>{openFilter ? t("product.hideFilter") : t("product.showFilter")}</span>
                </div>
              )}
              {filterLength === 0 && !state.loading && <BackButton />}
              {!isSearchPage && (
                <div className="filter-sort">
                  {matchingItems ? (
                    <>
                      <div className="total-item-count">
                        {t("product.items--quantity", {quantity: state.total})}
                        {" " + t("product.sortedBy")}
                      </div>
                      <ProductSortInput />
                    </>
                  ) : (
                    <div className="total-item-count">{t("product.items--quantity", {quantity: state.total})}</div>
                  )}
                </div>
              )}
            </div>
            <div
              className={`filter-list__reset-section ${openFilter && matchingItems ? "grid" : ""}`}
              ref={filterOverviewRef}
            >
              <SelectedFilterOverview />
            </div>
          </div>
          {openFilter && matchingItems && (
            <div className="filter-elements custom-scrollbar">
              <div className="filter-elements__container">
                <div className="bt-default" />
                {filterItems}
                {booleanFilterItems}
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        ref={productListRef}
        className={[
          "column-8",
          openFilter && matchingItems ? "column-m-5 column-l-9 pl-3 open-filter" : "column-l-12",
        ].join(" ")}
      >
        <FilterOverviewPlaceholder />
        <div className="product-list__wrapper">
          <ListItems />
        </div>
      </div>
    </>
  );
};

export const BackButton: React.FC = () => {
  const {t} = useTranslation();

  return (
    <div className="filter-list__back-btn">
      <span className="link link-icon" onClick={() => window.history.back()}>
        <i className="icon-left--blue mr-3"></i>
        {t("general.back")}
      </span>
    </div>
  );
};

export default forwardRef<RefType, ProductListFilterDesktopType>(ProductListFilterDesktop);
